
import React, { createContext, useContext, useState, useEffect } from "react";

type Theme = "light" | "dark" | "system";

interface UIContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isOnboardingComplete: boolean;
  setIsOnboardingComplete: (isComplete: boolean) => void;
  onboardingStep: number;
  setOnboardingStep: (step: number) => void;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
}

const UIContext = createContext<UIContextType | undefined>(undefined);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with values from localStorage or defaults
  const [theme, setTheme] = useState<Theme>(() => {
    const savedTheme = localStorage.getItem("theme") as Theme;
    return savedTheme || "system";
  });
  
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(() => {
    return localStorage.getItem("onboardingComplete") === "true";
  });
  
  const [onboardingStep, setOnboardingStep] = useState(() => {
    return parseInt(localStorage.getItem("onboardingStep") || "0");
  });
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Persist state changes to localStorage
  useEffect(() => {
    localStorage.setItem("theme", theme);
  }, [theme]);
  
  useEffect(() => {
    localStorage.setItem("onboardingComplete", isOnboardingComplete.toString());
  }, [isOnboardingComplete]);
  
  useEffect(() => {
    localStorage.setItem("onboardingStep", onboardingStep.toString());
  }, [onboardingStep]);
  
  // Apply theme to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }
  }, [theme]);
  
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);
  
  return (
    <UIContext.Provider
      value={{
        theme,
        setTheme,
        isOnboardingComplete,
        setIsOnboardingComplete,
        onboardingStep,
        setOnboardingStep,
        isModalOpen,
        openModal,
        closeModal,
      }}
    >
      {children}
    </UIContext.Provider>
  );
};

export const useUI = (): UIContextType => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return context;
};
